import { CreateDomainCommand } from '../../bounded-contexts/strategic-design/application/commands/CreateDomainCommand';
import { UpdateDomainCommand } from '../../bounded-contexts/strategic-design/application/commands/UpdateDomainCommand';
import { CreateDomainHandler } from '../../bounded-contexts/strategic-design/application/handlers/CreateDomainHandler';
import { UpdateDomainHandler } from '../../bounded-contexts/strategic-design/application/handlers/UpdateDomainHandler';
import { GetDomainHandler } from '../../bounded-contexts/strategic-design/application/handlers/GetDomainHandler';
import { ListDomainsHandler } from '../../bounded-contexts/strategic-design/application/handlers/ListDomainsHandler';
import { GetDomainQuery, ListDomainsQuery } from '../../bounded-contexts/strategic-design/application/queries/GetDomainQuery';
import { DomainViewModel, DomainSummaryViewModel, DomainStatsViewModel } from '../../bounded-contexts/strategic-design/interface/view-models/DomainViewModel';
import { CreateDomainRequest, UpdateDomainRequest, GetDomainsQuery } from '../../bounded-contexts/strategic-design/interface/dto/DomainRequestDTOs';

/**
 * Domain Application Service
 * 
 * Orchestrates domain operations and provides the boundary between
 * the API layer and the domain/application layers.
 * 
 * FIXED: No more Inversify - direct constructor injection following party-manager pattern.
 * Preserves the excellent CQRS architecture with proper handlers.
 */
export class DomainApplicationService {
    constructor(
        private readonly createDomainHandler: CreateDomainHandler,
        private readonly updateDomainHandler: UpdateDomainHandler,
        private readonly getDomainHandler: GetDomainHandler,
        private readonly listDomainsHandler: ListDomainsHandler
    ) {}

    /**
     * Create a new domain
     */
    async createDomain(request: CreateDomainRequest): Promise<DomainViewModel> {
        const command = new CreateDomainCommand(
            {
                name: request.name,
                description: request.description,
                vision: request.vision,
                ubiquitousLanguage: {}
            },
            'api-session',
            'api-user'
        );

        const result = await this.createDomainHandler.handle(command);
        
        return this.mapToDomainViewModel(result);
    }

    /**
     * List all domains (simple interface for MCP tools)
     */
    async listDomains(): Promise<DomainSummaryViewModel[]> {
        const defaultQuery: GetDomainsQuery = {
            status: 'active' as const,
            offset: 0,
            limit: 1000,
            search: '',
            includeStats: false
        };
        return this.getDomains(defaultQuery);
    }

    /**
     * Get all domains with optional filtering
     */
    async getDomains(query: GetDomainsQuery): Promise<DomainSummaryViewModel[]> {
        const listQuery = new ListDomainsQuery(
            {
                page: Math.floor(query.offset / query.limit) + 1,
                pageSize: query.limit,
                searchTerm: query.search,
                includeArchived: false
            },
            'api-session',
            'api-user'
        );

        const result = await this.listDomainsHandler.handle(listQuery);
        
        return result.items.map((domain: any) => ({
            id: domain.domainId || domain.id,
            name: domain.name,
            description: domain.description || '',
            status: this.mapDomainStatus(domain.status || 'active'),
            boundedContextCount: domain.boundedContextCount || 0,
            lastUpdated: domain.updatedAt || domain.createdAt
        }));
    }

    /**
     * Get a specific domain by ID
     */
    async getDomain(domainId: string): Promise<DomainViewModel> {
        const query = new GetDomainQuery(domainId, 'api-session', 'api-user');
        const result = await this.getDomainHandler.handle(query);
        
        return this.mapToDomainViewModel(result);
    }

    /**
     * Update an existing domain
     */
    async updateDomain(
        domainId: string,
        request: UpdateDomainRequest
    ): Promise<DomainViewModel> {
        const command = new UpdateDomainCommand(
            domainId,
            {
                name: request.name,
                description: request.description,
                vision: request.vision,
                ubiquitousLanguage: {}
            },
            'api-session',
            'api-user'
        );

        const result = await this.updateDomainHandler.handle(command);
        
        return this.mapToDomainViewModel(result);
    }

    /**
     * Get domain statistics
     */
    async getDomainStats(): Promise<DomainStatsViewModel> {
        const listQuery = new ListDomainsQuery(
            { page: 1, pageSize: 1000, includeArchived: true },
            'api-session',
            'api-user'
        );
        const result = await this.listDomainsHandler.handle(listQuery);
        
        const domains = result.items as any[];
        const activeDomains = domains.filter((d: any) => d.status !== 'deprecated');
        
        const avgBoundedContexts = domains.length > 0 
            ? domains.reduce((sum: any, d: any) => sum + (d.boundedContextCount || 0), 0) / domains.length 
            : 0;

        const mostComplex = domains.reduce((max: any, current: any) => 
            (current.boundedContextCount || 0) > (max.boundedContextCount || 0) ? current : max, 
            domains[0] || { id: '', name: 'N/A', boundedContextCount: 0 }
        );

        // Calculate recent activity (simplified - would normally use date filters)
        const recentDomains = domains.filter((d: any) => {
            const updatedDate = new Date(d.updatedAt || d.createdAt);
            const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
            return updatedDate > sevenDaysAgo;
        });

        return {
            totalDomains: domains.length,
            activeDomains: activeDomains.length,
            avgBoundedContextsPerDomain: Math.round(avgBoundedContexts * 10) / 10,
            mostComplexDomain: {
                id: mostComplex.id,
                name: mostComplex.name,
                complexity: mostComplex.boundedContextCount || 0
            },
            recentActivity: {
                domainsCreated: recentDomains.filter((d: any) => !d.updatedAt || d.updatedAt === d.createdAt).length,
                domainsUpdated: recentDomains.filter((d: any) => d.updatedAt && d.updatedAt !== d.createdAt).length,
                period: 'last_7_days'
            }
        };
    }

    /**
     * Map internal domain result to View Model
     */
    private mapToDomainViewModel(result: any): DomainViewModel {
        return {
            id: result.id || result.domainId,
            name: result.name,
            description: result.description,
            vision: result.vision,
            status: this.mapDomainStatus(result.status),
            createdAt: result.createdAt || new Date().toISOString(),
            updatedAt: result.updatedAt || result.createdAt || new Date().toISOString(),
            boundedContexts: {
                total: result.boundedContextCount || 0,
                active: result.activeBoundedContexts || 0
            },
            aggregates: {
                total: result.aggregateCount || 0,
                designed: result.designedAggregates || 0
            },
            maturity: this.calculateMaturity(result),
            complexity: this.calculateComplexity(result)
        };
    }

    /**
     * Map domain status from internal to external representation
     */
    private mapDomainStatus(internalStatus: string | undefined): 'draft' | 'active' | 'deprecated' {
        switch (internalStatus) {
            case 'DRAFT': return 'draft';
            case 'ACTIVE': return 'active';
            case 'DEPRECATED': return 'deprecated';
            default: return 'draft';
        }
    }

    /**
     * Calculate domain maturity based on its characteristics
     */
    private calculateMaturity(domain: any): 'emerging' | 'established' | 'legacy' {
        const boundedContexts = domain.boundedContextCount || 0;
        const age = domain.createdAt ? Date.now() - new Date(domain.createdAt).getTime() : 0;
        const daysOld = age / (1000 * 60 * 60 * 24);

        if (daysOld < 30 || boundedContexts < 2) return 'emerging';
        if (daysOld > 365 && domain.status === 'DEPRECATED') return 'legacy';
        return 'established';
    }

    /**
     * Calculate domain complexity based on structure
     */
    private calculateComplexity(domain: any): 'low' | 'medium' | 'high' {
        const boundedContexts = domain.boundedContextCount || 0;
        const aggregates = domain.aggregateCount || 0;
        
        const complexityScore = boundedContexts * 2 + aggregates;
        
        if (complexityScore < 5) return 'low';
        if (complexityScore < 15) return 'medium';
        return 'high';
    }
}